import {
    BATTLE_PENALTY_MULTIPLIER,
    BATTLE_PRIORITY_BONUS_MULTIPLIER,
    RESOURCE_NAMES,
    getShipDefinition,
    type BattleCombatLogEntry,
    type BattleReport,
    type BattleRoundSnapshot,
    type BattleShipStats,
    type BattleUnitGroup,
    type PlanetShips,
    type PlayerRace,
    type ShipId,
} from '@kaeliux/shared';
import { sidePassiveBonusPercents } from '@kaeliux/game-engine';
import { getShipIcon } from '../utils/shipIcons';
import ResourceQuantities from './ResourceQuantities';
import { Badge } from './ui/Badge';

type PassiveBonus = { bonusLife: number; armorBoost: number };

/** "+X% Life, +Y% Armor" fleet bonuses (Bonus Life / Armor Boost) in force for one round, or null if none. */
function passiveBonusLine(label: string, bonus: PassiveBonus): string | null {
    const parts: string[] = [];
    if (bonus.bonusLife > 0) parts.push(`+${Math.round(bonus.bonusLife * 100)}% Life`);
    if (bonus.armorBoost > 0) parts.push(`+${Math.round(bonus.armorBoost * 100)}% Armor`);
    return parts.length ? `${label} fleet bonus: ${parts.join(', ')}.` : null;
}

const PRIORITY_BONUS_PCT = Math.round((BATTLE_PRIORITY_BONUS_MULTIPLIER - 1) * 100); // +50%
const PENALTY_PCT = Math.round((BATTLE_PENALTY_MULTIPLIER - 1) * 100); // -30%

function shipName(race: PlayerRace, shipId: ShipId): string {
    return getShipDefinition(race, shipId).name;
}

function sideRace(report: BattleReport, side: 'attacker' | 'defender'): PlayerRace {
    return side === 'attacker' ? report.attackerRace : report.defenderRace;
}

function otherSide(side: 'attacker' | 'defender'): 'attacker' | 'defender' {
    return side === 'attacker' ? 'defender' : 'attacker';
}

/** Each ship reads by its own race; reports stored before the per-ship race fields fall back to the side's race. */
function firingRaceOf(entry: BattleCombatLogEntry, report: BattleReport): PlayerRace {
    return entry.firingRace ?? sideRace(report, entry.firingSide);
}

function targetRaceOf(entry: BattleCombatLogEntry, report: BattleReport): PlayerRace {
    return entry.targetRace ?? sideRace(report, otherSide(entry.firingSide));
}

/** Native multi-line tooltip: population (hangar) + this ship's resolved attack/life. */
function shipTooltip(race: PlayerRace, shipId: ShipId, count: number, stats: BattleShipStats | undefined): string {
    const def = getShipDefinition(race, shipId);
    const attack = Math.round(stats?.attack ?? def.attack) * count;
    const life = Math.round(stats?.life ?? def.life) * count;
    return `${def.name}\nAttack: ${attack.toLocaleString()}\nLife: ${life.toLocaleString()}`;
}

/** Compact ship-count table for one side's forces, from `BattleUnitGroup[]` (already has count/shipId + resolved perUnit stats). */
function BattleForcesTable({ groups, label }: { groups: BattleUnitGroup[]; label: string }) {
    const alive = groups.filter((g) => g.count > 0);
    return (
        <div className="flex-1 min-w-0">
            <h4 className="text-content-muted text-xs font-semibold uppercase tracking-wide mb-1">{label}</h4>
            {alive.length === 0 ? (
                <p className="text-content-muted text-xs">No forces.</p>
            ) : (
                <div className="flex flex-col gap-1">
                    {alive.map((g, i) => {
                        const def = getShipDefinition(g.race, g.shipId);
                        return (
                            <div key={`${g.race}-${g.shipId}-${i}`} className="flex items-center gap-1.5 text-xs text-content-primary" title={shipTooltip(g.race, g.shipId, g.count, g.perUnit)}>
                                <img src={getShipIcon(g.race, g.shipId)} alt={def.name} className="w-16 h-16 object-contain shrink-0" />
                                <span>{g.count.toLocaleString()} {def.name}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/** Final survivors table, from a raw `PlanetShips` count map (no perUnit — resolved stats come from `statsById`). */
function SurvivorsTable({ ships, race, statsById, label }: { ships: PlanetShips; race: PlayerRace; statsById: Map<ShipId, BattleShipStats>; label: string }) {
    const entries = (Object.entries(ships) as [string, number][])
        .map(([id, count]) => [Number(id) as ShipId, count ?? 0] as const)
        .filter(([, count]) => count > 0);
    return (
        <div className="flex-1 min-w-0">
            <h4 className="text-content-muted text-xs font-semibold uppercase tracking-wide mb-1">{label}</h4>
            {entries.length === 0 ? (
                <p className="text-status-danger text-xs">Fleet destroyed.</p>
            ) : (
                <div className="flex flex-col gap-1">
                    {entries.map(([shipId, count]) => {
                        const def = getShipDefinition(race, shipId);
                        return (
                            <div key={shipId} className="flex items-center gap-1.5 text-xs text-content-primary" title={shipTooltip(race, shipId, count, statsById.get(shipId))}>
                                <img src={getShipIcon(race, shipId)} alt={def.name} className="w-16 h-16 object-contain shrink-0" />
                                <span>{count.toLocaleString()}× {def.name}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function combatLogLine(entry: BattleCombatLogEntry, report: BattleReport): string {
    const firingRace = firingRaceOf(entry, report);
    const targetRace = targetRaceOf(entry, report);
    const firer = `${entry.firingCount.toLocaleString()} ${shipName(firingRace, entry.firingShipId)} (${entry.firingSide})`;
    const target = shipName(targetRace, entry.targetShipId);

    // Freezing is shown ONLY here, inline with the attacks — never as a round event.
    if (entry.wasFrozen) {
        return `${firer} is frozen and cannot attack this round.`;
    }

    const firingDef = getShipDefinition(firingRace, entry.firingShipId);
    const bonusPenalty = firingDef.bonusTargets?.includes(entry.targetShipId)
        ? `+${PRIORITY_BONUS_PCT}% vs ${target}`
        : firingDef.penaltyTargets?.includes(entry.targetShipId)
            ? `${PENALTY_PCT}% vs ${target}`
            : null;
    const flags = [
        bonusPenalty,
        entry.wasCritical && 'critical hit',
        entry.wasArmorIgnored && 'armor ignored',
    ].filter(Boolean).join(', ');
    const suffix = flags ? ` (${flags})` : '';
    const destroyed = entry.unitsDestroyed > 0 ? ` ${entry.unitsDestroyed.toLocaleString()} ${target} destroyed.` : '';
    const armorText = `${(entry.targetArmorPercent * 100).toFixed(2)}% Armor`;
    return `${firer} hits ${target} for ${Math.round(entry.finalDamage).toLocaleString()} damage${suffix}. `
        + `${target} had ${Math.round(entry.targetLifeBefore).toLocaleString()} Life and ${armorText}.${destroyed}`;
}

/** Round-start bonuses (Mega Power) — shown BEFORE the attacks. */
function bonusEventLines(round: BattleRoundSnapshot, report: BattleReport): string[] {
    return round.events.flatMap((event) =>
        event.type === 'megaPower'
            ? [`${event.side === 'attacker' ? 'Attacker' : 'Defender'}'s ${shipName(event.sourceRace ?? sideRace(report, event.side), 7)} overcharges the fleet: +${Math.round(event.attackBonus * 100)}% attack this round.`]
            : [],
    );
}

/** End-of-round revivals — shown AFTER the attacks. */
function revivalEventLines(round: BattleRoundSnapshot, report: BattleReport): string[] {
    return round.events.flatMap((event) =>
        event.type === 'revival'
            ? [`${event.side === 'attacker' ? 'Attacker' : 'Defender'}'s ${shipName(event.sourceRace ?? sideRace(report, event.side), 7)} revives ${event.revivedCount.toLocaleString()} ${shipName(event.revivedRace ?? sideRace(report, event.side), event.revivedShipId)}.`]
            : [],
    );
}

/** Round-start freezes (Medusa) — shown BEFORE the attacks, alongside Mega Power. `event.side` is the side whose Medusa activated; the frozen ship type belongs to the OTHER side. */
function freezingEventLines(round: BattleRoundSnapshot, report: BattleReport): string[] {
    return round.events.flatMap((event) => {
        if (event.type !== 'freezing') return [];
        const sourceSide = event.side === 'attacker' ? 'Attacker' : 'Defender';
        const sourceRace = event.sourceRace ?? sideRace(report, event.side);
        const targetSide = event.side === 'attacker' ? 'Defender' : 'Attacker';
        const targetRace = event.frozenRace ?? sideRace(report, otherSide(event.side));
        return [`${sourceSide}'s ${shipName(sourceRace, 7)} freezes the ${targetSide}'s ${shipName(targetRace, event.frozenShipId)} — frozen and cannot attack this round.`];
    });
}

/** Whole ships destroyed this round, per side (a side loses what the OTHER side's hits destroyed). */
function roundLosses(round: BattleRoundSnapshot, report: BattleReport): { attacker: number; defender: number } {
    let attacker = 0;
    let defender = 0;
    for (const entry of round.entries) {
        const lost = entry.unitsDestroyed * getShipDefinition(targetRaceOf(entry, report), entry.targetShipId).hangar;
        if (entry.firingSide === 'attacker') defender += lost;
        else attacker += lost;
    }

    return { attacker, defender };
}

function RoundDetails({ round, report, defaultOpen, collapseAttacks }: { round: BattleRoundSnapshot; report: BattleReport; defaultOpen: boolean; collapseAttacks: boolean }) {
    const bonuses = bonusEventLines(round, report);
    const freezes = freezingEventLines(round, report);
    const revivals = revivalEventLines(round, report);
    const losses = roundLosses(round, report);
    const percentsFrom = report.passivesPerRound ? round : (report.rounds[0] ?? round);
    const passiveLines = [
        passiveBonusLine('Attacker', sidePassiveBonusPercents(percentsFrom.attackerGroups)),
        passiveBonusLine('Defender', sidePassiveBonusPercents(percentsFrom.defenderGroups)),
    ].filter((line): line is string => line !== null);

    const attackList = (
        <ol className="text-xs text-content-secondary flex flex-col gap-1">
            {round.entries.map((entry, i) => (
                <li key={i}>{combatLogLine(entry, report)}</li>
            ))}
        </ol>
    );

    return (
        <details className="border border-hairline rounded-panel" open={defaultOpen}>
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-content-primary hover:text-content-primary">
                Round {round.round}
            </summary>
            <div className="px-3 pb-3 flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                    <BattleForcesTable groups={round.attackerGroups} label="Attacker" />
                    <BattleForcesTable groups={round.defenderGroups} label="Defender" />
                </div>
                {passiveLines.length > 0 && (
                    <ul className="text-xs text-status-info flex flex-col gap-0.5">
                        {passiveLines.map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                )}
                {bonuses.length > 0 && (
                    <ul className="text-xs text-status-warning flex flex-col gap-0.5">
                        {bonuses.map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                )}
                {freezes.length > 0 && (
                    <ul className="text-xs text-status-info flex flex-col gap-0.5">
                        {freezes.map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                )}
                <details className="border border-hairline rounded-control" open={!collapseAttacks}>
                    <summary className="cursor-pointer px-2 py-1 text-xs font-semibold text-content-muted hover:text-content-primary">
                        Details ({round.entries.length})
                    </summary>
                    <div className="px-2 py-1">{attackList}</div>
                </details>
                {revivals.length > 0 && (
                    <ul className="text-xs text-status-success flex flex-col gap-0.5">
                        {revivals.map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                )}
                <span className="text-sm font-semibold text-content-secondary">Attacker lost population: {losses.attacker}</span>
                <span className="text-sm font-semibold text-content-secondary">Defender lost population: {losses.defender}</span>
            </div>
        </details>
    );
}

const OUTCOME_LABEL: Record<BattleReport['outcome'], string> = {
    attackerWins: 'Attacker Victory',
    defenderWins: 'Defender Victory',
    draw: 'Draw',
};

/**
 * Viewer-relative outcome tone — success when the viewer's own side won,
 * danger when it lost, regardless of whether they were attacker or defender.
 * `OUTCOME_LABEL` states the objective attacker/defender result either way;
 * without this, "Attacker Victory" rendered in success to a defender who was
 * just crushed reads as if they won.
 */
function outcomeToneForViewer(outcome: BattleReport['outcome'], isAttacker: boolean): 'success' | 'danger' | 'neutral' {
    if (outcome === 'draw') return 'neutral';
    const viewerWon = isAttacker ? outcome === 'attackerWins' : outcome === 'defenderWins';
    return viewerWon ? 'success' : 'danger';
}

/** Resolved per-unit stats keyed by shipId, from the first round's start-of-round groups (they carry that round's Bonus Life and Armor Boost, which change from round to round). */
function statsMap(groups: BattleUnitGroup[] | undefined): Map<ShipId, BattleShipStats> {
    const map = new Map<ShipId, BattleShipStats>();
    for (const g of groups ?? []) map.set(g.shipId, g.perUnit);
    return map;
}

/**
 * Full round-by-round battle report viewer (#19) — used by `BattleReportPage`
 * for real arrivals and by the Battle Simulator. Pass `collapseAttacks` (the
 * simulator does for the verbose SEQUENTIAL model) to nest each round's attack
 * list behind a collapsed toggle.
 */
export default function BattleReportView({ report, viewerUserId, collapseAttacks }: { report: BattleReport; viewerUserId: string; collapseAttacks?: boolean }) {
    const isAttacker = viewerUserId === report.attackerUserId;
    const attackerStats = statsMap(report.rounds[0]?.attackerGroups);
    const defenderStats = statsMap(report.rounds[0]?.defenderGroups);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-content-muted text-xs">
                    You were the {isAttacker ? 'attacker' : 'defender'} in this battle.
                </span>
            </div>
            <Badge tone={outcomeToneForViewer(report.outcome, isAttacker)} className="text-lg font-bold">
                {OUTCOME_LABEL[report.outcome]}
            </Badge>
            <div className="flex flex-col gap-2">
                <h3 className="text-content-secondary text-sm font-semibold">Combat Log</h3>
                {report.rounds.map((round) => (
                    <RoundDetails key={round.round} round={round} report={report} defaultOpen={round.round === 1} collapseAttacks={!!collapseAttacks} />
                ))}
            </div>

            <div className="flex flex-col gap-2 pt-2 border-t border-hairline">
                <h3 className="text-content-secondary text-sm font-semibold">Battle Results</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                    <SurvivorsTable ships={report.attackerSurvivors} race={report.attackerRace} statsById={attackerStats} label="Attacker" />
                    <SurvivorsTable ships={report.defenderSurvivors} race={report.defenderRace} statsById={defenderStats} label="Defender" />
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs pt-2 border-t border-hairline">
                <div>
                    <span className="text-content-muted block">Battle Points</span>
                    <span className="text-content-primary" data-testid="battle-points">
                        Attacker +{report.points.attackerBattlePoints.toLocaleString()} · Defender +{report.points.defenderBattlePoints.toLocaleString()}
                    </span>
                </div>
                <div>
                    <span className="text-content-muted block">Resource Points</span>
                    <span className="text-content-primary" data-testid="resource-points">
                        Attacker −{report.points.attackerResourcePointsLost.toLocaleString()} · Defender −{report.points.defenderResourcePointsLost.toLocaleString()}
                    </span>
                </div>
                <div>
                    <span className="text-content-muted block">{RESOURCE_NAMES.scrap}</span>
                    <span className="text-content-primary" data-testid="battle-scrap">{report.scrap > 0 ? `${Math.round(report.scrap).toLocaleString()}` : 'None'}</span>
                </div>
                <div>
                    <span className="text-content-muted block">Stolen Resources</span>
                    <span className="text-content-primary" data-testid="stolen-resources"><ResourceQuantities amounts={report.stolenResources} empty="None" /></span>
                </div>
            </div>

            {(report.demolitionPoints.attackerChance > 0 || report.demolitionPoints.defenderChance > 0) && (
                <p className="text-content-muted text-xs italic">
                    Building-destruction chance was calculated (attacker {Math.round(report.demolitionPoints.attackerChance * 100)}%, defender {Math.round(report.demolitionPoints.defenderChance * 100)}%) but isn&rsquo;t applied yet — coming in a follow-up.
                </p>
            )}
        </div>
    );
}
